import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Layout,
  Card,
  Button,
  Badge,
  Text,
  ResourceList,
  ResourceItem,
  Banner,
  InlineStack,
  BlockStack,
  Spinner,
} from '@shopify/polaris'
import { workflowApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import type { Workflow, WorkflowTrigger } from '../types'

const TRIGGER_LABELS: Record<WorkflowTrigger, string> = {
  MESSAGE_RECEIVED: 'Message Received',
  ORDER_CREATED: 'Order Created',
  ORDER_SHIPPED: 'Order Shipped',
  ORDER_DELIVERED: 'Order Delivered',
  CART_ABANDONED: 'Cart Abandoned',
  CUSTOMER_INACTIVE: 'Customer Inactive',
  CUSTOMER_REGISTERED: 'Customer Registered',
  TIME_BASED: 'Time Based',
  PAYMENT_FAILED: 'Payment Failed',
  SENTIMENT_NEGATIVE: 'Negative Sentiment',
  CONVERSATION_OPENED: 'Conversation Opened',
  CONVERSATION_CLOSED: 'Conversation Closed',
}


export default function WorkflowsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [loading, setLoading] = useState(true)
  const [analytics, setAnalytics] = useState<any>(null)

  useEffect(() => {
    if (user?.storeId) {
      fetchWorkflows()
      fetchAnalytics()
    } else {
      // If no store after initial load, stop showing loading spinner
      setLoading(false)
    }
  }, [user?.storeId])

  const fetchWorkflows = async () => {
    if (!user?.storeId) return

    try {
      const response = await workflowApi.getWorkflows(user.storeId)
      setWorkflows(response.data.data.workflows)
    } catch (error: any) {
      console.error('Failed to fetch workflows:', error)
      toast.error('Failed to load workflows')
    } finally {
      setLoading(false)
    }
  }

  const fetchAnalytics = async () => {
    if (!user?.storeId) return

    try {
      const response = await workflowApi.getAnalytics(user.storeId)
      setAnalytics(response.data.data)
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
    }
  }


  const formatDate = (date?: string) => {
    if (!date) return 'Never'
    return new Date(date).toLocaleString()
  }

  const getSuccessRate = (workflow: Workflow) => {
    if (workflow.executionCount === 0) return 0
    return Math.round((workflow.successCount / workflow.executionCount) * 100)
  }

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner size="large" />
      </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  if (!user?.storeId) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <Banner tone="critical">
                <Text variant="headingMd" as="h3">Store not found</Text>
                <Text variant="bodyMd" as="p">Please ensure you are logged in with a valid store.</Text>
              </Banner>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page
      title="Automation Workflows"
      subtitle="Create intelligent workflows to automate your customer communications"
      primaryAction={{
        content: 'Create Workflow',
        onAction: () => navigate('/workflows/new'),
      }}
      secondaryActions={[
        {
          content: 'Browse Templates',
          onAction: () => navigate('/workflows/templates'),
        },
      ]}
    >

      <Layout>
      {analytics && (
          <Layout.Section>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="bodySm" as="p" tone="subdued">Total Workflows</Text>
                  <Text variant="headingLg" as="h3">{analytics.summary.totalWorkflows}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="bodySm" as="p" tone="subdued">Active</Text>
                  <Text variant="headingLg" as="h3">{analytics.summary.activeWorkflows}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="bodySm" as="p" tone="subdued">Total Executions</Text>
                  <Text variant="headingLg" as="h3">{analytics.summary.totalExecutions}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="bodySm" as="p" tone="subdued">Success Rate</Text>
                  <Text variant="headingLg" as="h3">{analytics.summary.successRate}%</Text>
                </BlockStack>
              </Card>
            </div>
          </Layout.Section>
        )}

        <Layout.Section>
          {workflows.length === 0 ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h3">No workflows</Text>
                  <Text variant="bodyMd" as="p">Get started by creating a new workflow or browse our templates.</Text>
                  <InlineStack gap="300" blockAlign="center">
                    <Button onClick={() => navigate('/workflows/templates')}>Browse Templates</Button>
                    <Button variant="primary" onClick={() => navigate('/workflows/new')}>Create Workflow</Button>
                  </InlineStack>
                </BlockStack>
              </div>
            </Card>
          ) : (
            <Card>
              <ResourceList
                resourceName={{ singular: 'workflow', plural: 'workflows' }}
                items={workflows}
                renderItem={(item) => {
                  const workflow = item as Workflow
                  const successRate = getSuccessRate(workflow)
                  
                  return (
                    <ResourceItem
                      id={workflow.id}
                      onClick={() => navigate(`/workflows/${workflow.id}`)}
                      accessibilityLabel={`Edit ${workflow.name}`}
                    >
                      <InlineStack gap="200">
                        <Text variant="headingSm" as="h3">{workflow.name}</Text>
                        <Badge>{TRIGGER_LABELS[workflow.trigger]}</Badge>
                        <Badge tone={workflow.isActive ? 'success' : undefined}>
                          {workflow.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </InlineStack>
                      <Text variant="bodySm" as="p">{workflow.description}</Text>
                      <InlineStack gap="300">
                        <Text variant="bodySm" as="span">
                          {workflow.conditions.length} condition{workflow.conditions.length !== 1 ? 's' : ''}
                        </Text>
                        <Text variant="bodySm" as="span">•</Text>
                        <Text variant="bodySm" as="span">
                          {workflow.actions.length} action{workflow.actions.length !== 1 ? 's' : ''}
                        </Text>
                        <Text variant="bodySm" as="span">•</Text>
                        <Text variant="bodySm" as="span">{workflow.executionCount} executions</Text>
                        <Text variant="bodySm" as="span">•</Text>
                        <Text variant="bodySm" as="span" tone={successRate >= 80 ? 'success' : undefined}>
                          {successRate}% success
                        </Text>
                      </InlineStack>
                      <Text variant="bodySm" as="p">Last executed: {formatDate(workflow.lastExecutedAt)}</Text>
                    </ResourceItem>
                  )
                }}
              />
            </Card>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  )
}
